import * as React from 'react';
import clsx from 'clsx';
import { useTheme } from '../../../hooks/useTheme';
import styles from './BlockButton.module.css';

export enum ButtonType {
  button = 'button',
  submit = 'submit',
}

export enum ButtonTargetType {
  self = '_self',
  blank = '_blank',
}

export interface ButtonProps {
  onClick?: React.MouseEventHandler<Element>;
  href?: string;
  target?: string;
  type?: ButtonType;
  big?: boolean;
  disabled?: boolean;
  outline?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.HTMLProps<HTMLButtonElement>['style'];
  onMouseEnter?: React.HTMLProps<HTMLButtonElement>['onMouseEnter'];
  onMouseLeave?: React.HTMLProps<HTMLButtonElement>['onMouseLeave'];
}

const Button: React.FunctionComponent<ButtonProps> = ({
  onClick,
  href,
  target,
  type = ButtonType.button,
  big,
  disabled,
  outline,
  children,
  className = '',
  style,
  onMouseEnter,
  onMouseLeave,
}) => {
  const [isHover, setIsHover] = React.useState(false);

  const handleMouseEnter: React.HTMLProps<HTMLButtonElement>['onMouseEnter'] = (
    e,
  ) => {
    setIsHover(true);
    onMouseEnter && onMouseEnter(e);
  };
  const handleMouseLeave: React.HTMLProps<HTMLButtonElement>['onMouseLeave'] = (
    e,
  ) => {
    setIsHover(false);
    onMouseLeave && onMouseLeave(e);
  };

  const {
    buttonsPrimaryDefault: buttonStyleDefault,
    buttonsPrimaryDisabled: buttonStyleDisabled,
    buttonsPrimaryHover: buttonStyleHover,
    buttonsSecondaryDefault: outlineStyleDefault,
    buttonsSecondaryDisabled: outlineStyleDisabled,
    buttonsSecondaryHover: outlineStyleHover,
  } = useTheme();

  const sPrimary: React.CSSProperties = {
    ...buttonStyleDefault,
    ...(isHover
      ? (buttonStyleHover ?? {
          borderColor: '#286090',
          backgroundColor: '#286090',
          color: '#fff',
        })
      : {}),
    ...(disabled
      ? (buttonStyleDisabled ?? {
          borderColor: '#cccccc',
          backgroundColor: '#cccccc',
          cursor: 'not-allowed',
        })
      : {}),
  };

  const sOutline: React.CSSProperties = {
    ...outlineStyleDefault,
    ...(isHover
      ? (outlineStyleHover ?? {
          borderColor: '#286090',
          color: '#286090',
        })
      : {}),
    ...(disabled
      ? (outlineStyleDisabled ?? {
          borderColor: '#ccc',
          cursor: 'not-allowed',
          color: '#ccc',
        })
      : {}),
  };

  const classes = clsx({
    [className]: !!className,
    [styles.button]: true,
    [styles.buttonOutline]: outline,
  });

  const button = (
    <button
      style={{
        ...(outline ? sOutline : sPrimary),
        width: big ? '100%' : 'auto',
        ...style,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      onClick={href || disabled ? undefined : onClick}
      type={href ? undefined : type}
      className={classes}
    >
      {children}
    </button>
  );

  if (href) {
    return (
      <a
        href={href}
        target={target || ButtonTargetType.blank}
        rel="noreferrer noopener"
        onClick={onClick}
        style={style}
      >
        {button}
      </a>
    );
  }

  return button;
};

export default Button;
